import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import Dialog from "@mui/material/Dialog";
import { type FormEvent, useId, useState } from "react";
import WalletSelect from "@/components/campaignBuilder/WalletSelect";
import { GfgButton } from "@/components/ui/GfgButton";
import { GfgTextField } from "@/components/ui/GfgTextField";
import {
  CAMPAIGN_NAME_MAX_LENGTH,
  CAMPAIGN_NAME_TEXT,
  FUNDING_WALLET_TEXT,
} from "@/constants/campaignBuilder";
import type { CampaignNameModalMode } from "@/interfaces/CampaignBuilder";
import type { FundingWallet } from "@/interfaces/campaignDraft";
import { isValidCampaignName, normalizeCampaignName } from "@/utils/campaignName-helper";

interface CampaignNameModalProps {
  /** The parent mounts this only while a modal is open and keys it by mode, so it always renders
   *  `open` and `initialName` only seeds the draft. */
  mode: CampaignNameModalMode;
  /** Prefill for rename, "" for create. */
  initialName: string;
  /** Resolves true once saved (the parent then unmounts this); false keeps it open, text intact.
   *  `groupId` is the chosen funding wallet, null when the modal doesn't ask for one. */
  onSubmit: (name: string, groupId: number | null) => Promise<boolean>;
  /** Rename only: Esc, backdrop and Cancel discard the edit. */
  onCancel: () => void;
  /** Create mode on a multi-group plan: the wallets to choose from. Absent → no wallet field. */
  wallets?: FundingWallet[];
  walletsLoading?: boolean;
  /** The wallet list failed to load: say so and offer a retry, since this modal can't be closed. */
  walletsError?: boolean;
  onRetryWallets?: () => void;
  /** Preselection (the caller's only wallet). */
  initialGroupId?: number | null;
}

/**
 * The "name your campaign" form (GFG-413). Same Dialog-plus-form shell as
 * `reports/FixBouncedEmailModal.tsx`. Create is required, so it cannot be dismissed (no Cancel,
 * Esc and backdrop do nothing); rename can. The raw draft is passed up: the context normalizes.
 */
export default function CampaignNameModal({
  mode,
  initialName,
  onSubmit,
  onCancel,
  wallets,
  walletsLoading = false,
  walletsError = false,
  onRetryWallets,
  initialGroupId = null,
}: CampaignNameModalProps) {
  const [draft, setDraft] = useState(initialName);
  const [pickedGroupId, setPickedGroupId] = useState<number | null>(null);
  const [pending, setPending] = useState(false);

  const titleId = useId();
  const helperId = useId();
  const nameId = useId();
  const walletHintId = useId();

  const isCreate = mode === "create";
  const askWallet = isCreate && wallets !== undefined;
  const groupId = askWallet ? (pickedGroupId ?? initialGroupId) : null;
  const tooLong = normalizeCampaignName(draft).length > CAMPAIGN_NAME_MAX_LENGTH;
  const canSubmit = isValidCampaignName(draft) && (!askWallet || groupId !== null) && !pending;

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!canSubmit) return;

    setPending(true);
    let saved = false;
    try {
      saved = await onSubmit(draft, groupId);
    } finally {
      if (!saved) setPending(false);
    }
  }

  function focusName() {
    const input = document.getElementById(nameId);
    if (!(input instanceof HTMLInputElement)) return;
    input.focus();
    input.select();
  }

  return (
    <Dialog
      open
      onClose={isCreate || pending ? undefined : onCancel}
      disableEscapeKeyDown={isCreate || pending}
      aria-labelledby={titleId}
      aria-describedby={helperId}
      disableScrollLock
      maxWidth={false}
      slotProps={{
        // Focus has to land after the open transition: the Dialog's FocusTrap pulls focus onto the
        // paper once it entered, overwriting a mount-time `.focus()`.
        transition: { onEntered: focusName },
        paper: {
          sx: {
            borderRadius: "12px",
            p: "32px",
            m: 2,
            width: 440,
            maxWidth: "calc(100vw - 32px)",
            boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
          },
        },
      }}
    >
      <form onSubmit={handleSubmit} noValidate>
        <h2
          id={titleId}
          className="mb-1.5 text-left font-crimson font-normal text-2xl text-rodeo-black"
        >
          {!isCreate
            ? CAMPAIGN_NAME_TEXT.renameTitle
            : askWallet
              ? CAMPAIGN_NAME_TEXT.createWithWalletTitle
              : CAMPAIGN_NAME_TEXT.createTitle}
        </h2>
        <p id={helperId} className="mb-5 font-lato text-graphite text-sm">
          {askWallet ? CAMPAIGN_NAME_TEXT.helperWithWallet : CAMPAIGN_NAME_TEXT.helper}
        </p>

        <GfgTextField
          id={nameId}
          label={CAMPAIGN_NAME_TEXT.label}
          autoComplete="off"
          maxLength={CAMPAIGN_NAME_MAX_LENGTH}
          showCounter
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          error={tooLong}
          errorText={tooLong ? CAMPAIGN_NAME_TEXT.tooLong : undefined}
        />

        {askWallet && (
          <div className="mt-5">
            <WalletSelect
              label={FUNDING_WALLET_TEXT.fundingLabel}
              wallets={wallets}
              value={groupId}
              onChange={setPickedGroupId}
              disabled={walletsLoading || pending}
              aria-describedby={walletHintId}
            />
            {walletsError ? (
              <p
                id={walletHintId}
                role="alert"
                className="mt-2 font-lato text-[12px] text-good-wine"
              >
                {FUNDING_WALLET_TEXT.loadError}{" "}
                <button type="button" className="underline" onClick={onRetryWallets}>
                  {FUNDING_WALLET_TEXT.retry}
                </button>
              </p>
            ) : !walletsLoading && wallets.length === 0 ? (
              <p
                id={walletHintId}
                role="alert"
                className="mt-2 font-lato text-[12px] text-good-wine"
              >
                {FUNDING_WALLET_TEXT.noneAvailable}
              </p>
            ) : (
              <p id={walletHintId} className="mt-2 font-lato text-[12px] text-graphite">
                {FUNDING_WALLET_TEXT.modalHint}
              </p>
            )}
          </div>
        )}

        <div className="mt-6 flex items-center justify-end gap-3">
          {!isCreate && (
            <GfgButton type="button" variant="secondary" onClick={onCancel} disabled={pending}>
              {CAMPAIGN_NAME_TEXT.cancel}
            </GfgButton>
          )}
          <GfgButton
            type="submit"
            variant="primary"
            disabled={!canSubmit}
            endIcon={isCreate ? <ArrowForwardIcon sx={{ fontSize: 14 }} /> : undefined}
          >
            {isCreate ? CAMPAIGN_NAME_TEXT.next : CAMPAIGN_NAME_TEXT.save}
          </GfgButton>
        </div>
      </form>
    </Dialog>
  );
}
